import React, { useEffect, PropsWithChildren, useState } from 'react';
import { Iffy } from '@alucio/lux-ui';
import { Hub, Logger } from '@aws-amplify/core';
import subDays from 'date-fns/subDays';
import LoadingScreen from '../../screens/Loading'
import Auth from '@aws-amplify/auth'
import { DataStore, syncExpression } from '@aws-amplify/datastore';
import {
  useDataStoreSubscription,
  useSyncMachineSubscription,
  useOfflineManifestSubscription,
} from 'src/state/datastore/subscriptions';
import hydrate from '../../state/datastore/hydrate';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useSelector } from 'react-redux';
import { RootState } from 'src/state/redux/store';
import { validate as uuidValidate } from 'uuid';
import {
  AttachedFile,
  CustomDeck,
  Document,
  DocumentVersion,
  EmailTemplate,
  Folder,
  Tenant,
  User,
  Meeting,
} from '@alucio/aws-beacon-amplify/src/models'
import { useLDClient } from 'launchdarkly-react-client-sdk';

const logger = new Logger('UserInit', 'INFO');
// Having tenantId declared at this level let us update the value when needed
// and ensure that the syncExpressions has the correct value
// when they are executed at DataStore.start() call
let tenantId = ''
let createdBy = ''
const UserInit: React.FC<PropsWithChildren<{}>> = (props) => {
  const { children } = props;
  const isHydrated = useSelector((state: RootState) => state.document.hydrated && state.emailTemplate.hydrated);

  const [dataStoreReady, setDataStoreReady] = useState(isHydrated);
  const dataStoreSubscribed = useDataStoreSubscription(dataStoreReady);
  const { isOfflineEnabled } = useAppSettings();
  const syncMachineSubscribed = useSyncMachineSubscription(dataStoreReady, isOfflineEnabled);
  useOfflineManifestSubscription()
  const ldClient = useLDClient()

  useEffect(() => {
    let removeListener: () => void | undefined
    const init = async () => {
      if (isOfflineEnabled !== undefined && ldClient && !isHydrated) {
        removeListener = Hub.listen('datastore', async (capsule) => {
          const { payload: { event } } = capsule;
          if (event === 'ready') {
            // Datastore is done syncing now populate redux
            logger.debug('Got ready event from datastore')
            await hydrate(isOfflineEnabled, ldClient);
            setDataStoreReady(true);
            logger.debug('Done hydrating redux')
            removeListener();
          }
        });

        // Configure datastore to use the tenantId GSI when querying dynamoDB rather than a scan
        let cognitoUser;
        const thirtyDaysAgo = subDays(new Date(), 30).toISOString();
        try {
          cognitoUser = await Auth.currentAuthenticatedUser();
        } catch (e) {
          cognitoUser = JSON.parse(localStorage.getItem('amplify-latest-user-attributes') ?? '');
        }
        tenantId = cognitoUser?.attributes['custom:org_id']
        createdBy = cognitoUser?.attributes['custom:user_id']
        const syncExpressions = uuidValidate(tenantId) ? [
          syncExpression(AttachedFile, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(CustomDeck, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(Document, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(DocumentVersion, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(EmailTemplate, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(Folder, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(Tenant, () => {
            return obj => obj.id('eq', tenantId)
          }),
          syncExpression(User, () => {
            return obj => obj.tenantId('eq', tenantId)
          }),
          syncExpression(Meeting, () => {
            return obj => obj.createdBy('eq', createdBy).startTime('gt', thirtyDaysAgo);
          }),
        ] : [];

        DataStore.configure({
          syncPageSize: 150,
          errorHandler: (error) => logger.warn('Datastore Sync Error', error),
          syncExpressions: syncExpressions,
        })

        // Start the DataStore, this kicks-off the sync process.
        logger.debug('Calling DataStore.start')
        DataStore.start();
      }
    }
    init()
    return () => {
      logger.debug('Hub removeListener called')
      removeListener?.();
    }
  }, [isOfflineEnabled, ldClient, isHydrated]);

  const isReady = (
    dataStoreReady &&
    dataStoreSubscribed &&
    (!isOfflineEnabled || syncMachineSubscribed)
  )

  return (
    <>
      <Iffy is={isReady}>{children}</Iffy>
      <Iffy is={!isReady}><LoadingScreen /></Iffy>
    </>
  );
};

export default UserInit;
